import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SEGMENT_FEATURES } from 'Config';
import { VehicleFuelViewModel } from 'api/vehicles/models/VehicleViewModel';
import { VehicleFuelCatalogItem } from 'api/vehicles/models/VehicleFuelCatalogItem';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiltersFormModel } from 'screens/dashboard/filters/FiltersView';
import useDashboardLayout, { buildAssetLayoutAdapter } from 'screens/dashboard/hooks/useDashboardLayout';
import DashboardCanvas from 'screens/dashboard/components/DashboardCanvas';
import DashboardService from 'api/dashboard/DashboardService';
import UsersService from 'api/users/UsersService';
import {
	ASSET_WIDGET_IDS,
	DEFAULT_ASSET_LAYOUT,
	ASSET_LAYOUT_VERSION,
	getEnabledCategoriesForUser,
	computeAssetAvailableWidgetIds,
	sanitizeVisibility,
} from 'screens/dashboard/widgetRegistry';
import { DashboardGroup, DashboardLayoutConfig, WidgetDefinition } from 'screens/dashboard/types/DashboardLayoutTypes';
import ReplicateLayoutModal from 'screens/dashboard/components/ReplicateLayoutModal';
import Button from 'common/components/button/Button';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import styles from './CarDashboardTab.module.scss';
import dashboardStyles from 'screens/dashboard/DashboardScreen.module.scss';

// Widgets de combustível "por ativo" que devem ficar fixos ao ativo atual (sem seletor de viaturas).
const LOCKED_VEHICLE_WIDGETS = new Set(['fuelConsumptionPerAsset', 'electricConsumptionPerAsset', 'fuelExpensesPerAsset']);

type Props = {
	hasTrackingDeviceId?: boolean;
	reservationAvailable?: boolean;
	hasContractedKm?: boolean;
	vehicleFuels?: VehicleFuelViewModel[];
	tourReRender: () => void;
	segmentFeatures: string[];
	registrationNumber?: string;
	hasFuelLevel?: boolean;
	onMonthlyCostDashboardClick?: (filters: FiltersFormModel, startDate?: Date) => void;
}

const CarDashboardTab = ({ hasTrackingDeviceId, reservationAvailable, hasContractedKm, vehicleFuels, tourReRender, segmentFeatures, registrationNumber, hasFuelLevel, onMonthlyCostDashboardClick }: Props) => {
	const { t } = useTranslation();
	const { vehicleId } = useParams<{ vehicleId: string }>();
	const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
	const userPolicies = userProfile?.policies ?? [];
	const dashboardGroup: DashboardGroup = userProfile?.dashboardGroup ?? 'FULL';
	const enabledCategories = useMemo(() => getEnabledCategoriesForUser(userPolicies), [userPolicies]);
	// Coluna "Localizador GPS" na replicação só faz sentido com a policy de GPS (igual aos widgets de GPS).
	const showGpsColumn = useMemo(() => UsersService.hasPolicies(userPolicies, ['SETTINGUP_GPS_READ' as any]), [userPolicies]);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isReplicateOpen, setIsReplicateOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Self-contained (igual ao painel principal): o react-grid-layout no fluxo normal parte a
	// cadeia de altura e gera duplo scroll / espaço vazio. Por isso, enquanto o tab está montado,
	// travamos o scroll exterior e damos à grelha uma área própria (scrollArea absoluta) que
	// preenche o espaço até ao fundo do viewport — só os widgets rolam; cabeçalho/sidebar fixos.
	useLayoutEffect(() => {
		const root = document.getElementById('root');
		const scrollHere = document.getElementById('scrollHere');
		const saved = {
			rootOverflow: root?.style.overflowY ?? '',
			scrollOverflow: scrollHere?.style.overflow ?? '',
		};
		if (scrollHere) {
			scrollHere.scrollTop = 0;
			scrollHere.style.overflow = 'hidden';
		}
		if (root) root.style.overflowY = 'clip';

		const resize = () => {
			const el = wrapperRef.current;
			if (!el) return;
			const top = el.getBoundingClientRect().top;
			el.style.height = `${Math.max(320, window.innerHeight - top)}px`;
		};
		resize();
		// Segunda medição após o paint, para apanhar qualquer ajuste tardio do layout acima.
		const raf = requestAnimationFrame(resize);
		window.addEventListener('resize', resize);

		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('resize', resize);
			if (scrollHere) scrollHere.style.overflow = saved.scrollOverflow;
			if (root) root.style.overflowY = saved.rootOverflow;
		};
	}, []);

	// Layout por (utilizador, ativo): mesmo motor do painel principal, mas vocacionado ao ativo.
	const layoutAdapter = useMemo(
		() => buildAssetLayoutAdapter(vehicleId, DEFAULT_ASSET_LAYOUT, ASSET_LAYOUT_VERSION, ASSET_WIDGET_IDS),
		[vehicleId],
	);
	const layoutApi = useDashboardLayout(layoutAdapter);

	const hasKmFeature = (segmentFeatures?.findIndex(x => x == SEGMENT_FEATURES.KM) ?? -1) >= 0;

	// Capacidades deste ativo — para o aviso de incompatibilidade no "Copiar de" (este ativo = destino).
	const currentAssetCaps = useMemo(() => ({
		hasGps: !!hasTrackingDeviceId,
		reservationAvailable: !!reservationAvailable,
		hasContractedKm: !!hasContractedKm,
		hasKmFeature,
		fuels: (vehicleFuels ?? []).map(f => f.fuelName).filter(Boolean) as string[],
	}), [hasTrackingDeviceId, reservationAvailable, hasContractedKm, hasKmFeature, vehicleFuels]);

	// Allowlist do ativo: esconde do picker os widgets que não fazem sentido para ESTE carro,
	// segundo as suas capacidades (GPS, reservas, contrato com km, combustível real). A mesma
	// função é reusada para sanitizar "Copiar de"/"Replicar para" (ver widgetRegistry).
	const availableWidgetIds = useMemo(
		() => {
			const ids = computeAssetAvailableWidgetIds({
				hasGps: hasTrackingDeviceId,
				reservationAvailable,
				hasKmFeature,
				hasContractedKm,
				fuelNames: (vehicleFuels ?? []).map(f => f.fuelName),
			});
			// Sem leituras de nível no CAN → o ativo não tem o sensor certo; esconder o widget
			// "Oscilação de combustível" do picker.
			if (!hasFuelLevel) ids.delete('fuelTankTimeline');
			return ids;
		},
		[hasTrackingDeviceId, hasKmFeature, reservationAvailable, hasContractedKm, vehicleFuels, hasFuelLevel],
	);

	useEffect(() => {
		tourReRender();
		// eslint-disable-next-line
	}, []);

	// Widgets comportam-se exatamente como no painel principal (cada um gere o seu período);
	// o vehicleId é injetado via DashboardVehicleScopeContext (vehicleScopeId no canvas).
	// Aqui só passamos props específicas: vehicleId direto (mapa GPS), lockedVehicleId
	// (widgets de combustível) e o callback do custo mensal.
	const buildWidgetProps = useMemo(
		() => (def: WidgetDefinition) => {
			const props: Record<string, unknown> = { vehicles: [], vehicleId, vehicleName: registrationNumber };
			if (LOCKED_VEHICLE_WIDGETS.has(def.id)) {
				props.lockedVehicleId = vehicleId;
			}
			if (def.id === 'monthlyCostChart' && onMonthlyCostDashboardClick) {
				const syntheticFilters: FiltersFormModel = { dateFrom: null, dateTo: null, vehicleId, timePeriod: null, inactives: null };
				props.onMonthlyCostDashboardClick = (x?: Date) => onMonthlyCostDashboardClick(syntheticFilters, x);
			}
			return props;
		},
		[vehicleId, registrationNumber, onMonthlyCostDashboardClick],
	);

	// JSON da disposição atual para "Replicar para": sanitizado contra as capacidades DESTE ativo,
	// para não propagar aos destinos flags `visible:true` de widgets que este ativo não mostra
	// (escondidos só no render). Senão, um destino capaz revelava widgets a mais.
	const currentLayoutJson = useMemo(
		() => JSON.stringify({ version: ASSET_LAYOUT_VERSION, widgets: sanitizeVisibility(layoutApi.layout, availableWidgetIds) }),
		[layoutApi.layout, availableWidgetIds],
	);

	// "Copiar de": vai buscar a disposição gravada do ativo de ORIGEM e aplica-a a ESTE ativo.
	// Sanitiza contra as capacidades da origem (não copiar widgets que a origem não mostra) e contra
	// as deste ativo (manter o JSON gravado honesto). Se a origem não tiver disposição, fica o default.
	const handleCopyFrom = useCallback(async (source: VehicleFuelCatalogItem) => {
		const response = await DashboardService.getAssetLayout(source.value);
		const raw = response?.layout ?? null;
		if (!raw) { layoutApi.importLayout(null); return; }
		const sourceAvailable = computeAssetAvailableWidgetIds({
			hasGps: source.hasGps,
			reservationAvailable: source.reservationAvailable,
			hasKmFeature: source.hasKmFeature,
			hasContractedKm: source.hasContractedKm,
			fuelNames: source.fuelsList,
		});
		// Só fica visível o que a ORIGEM mostrava E o que ESTE ativo consegue mostrar.
		const allowed = new Set(Array.from(sourceAvailable).filter(id => availableWidgetIds.has(id)));
		try {
			const config: DashboardLayoutConfig = JSON.parse(raw);
			const widgets = sanitizeVisibility(config.widgets ?? [], allowed);
			layoutApi.importLayout(JSON.stringify({ ...config, widgets }));
		} catch {
			// JSON inválido → deixa o importLayout cair no default do âmbito.
			layoutApi.importLayout(raw);
		}
	}, [layoutApi, availableWidgetIds]);

	const hasVisibleWidgets = layoutApi.layout.some(w => w.visible);

	return (
		<div id="second-step" ref={wrapperRef} className={styles.wrapper}>
			<div className={styles.headerContainer}>
				{/* Com widgets: botões no header (Gestão à esquerda, Widgets à direita). Vazio: os
				    mesmos dois botões aparecem no empty-state (ver emptyState* no DashboardCanvas). */}
				{hasVisibleWidgets && (
					<Button
						preset="secondary"
						size="small"
						text={t('dashboard.replicate.button') as string}
						onClick={() => setIsReplicateOpen(true)}
					/>
				)}
				{hasVisibleWidgets && (
					<button
						className={dashboardStyles.addWidgetButton}
						onClick={() => setIsDrawerOpen(true)}
					>
						{t('dashboard.drawer.widgets_button' as any)}
					</button>
				)}
			</div>
			<div className={styles.scrollWrapper}>
				<div className={styles.scrollArea}>
					<DashboardCanvas
						layoutApi={layoutApi}
						userPolicies={userPolicies}
						dashboardGroup={dashboardGroup}
						enabledCategories={enabledCategories}
						availableWidgetIds={availableWidgetIds}
						buildWidgetProps={buildWidgetProps}
						isDrawerOpen={isDrawerOpen}
						setIsDrawerOpen={setIsDrawerOpen}
						vehicleScopeId={vehicleId}
						emptyStatePrimaryLabel={t('dashboard.drawer.widgets_button' as any) as string}
						emptyStateAction={{ label: t('dashboard.replicate.button') as string, onClick: () => setIsReplicateOpen(true) }}
					/>
				</div>
			</div>
			<ReplicateLayoutModal
				isOpen={isReplicateOpen}
				onClose={() => setIsReplicateOpen(false)}
				currentVehicleId={vehicleId}
				layoutJson={currentLayoutJson}
					onCopyFrom={handleCopyFrom}
					hasLayout={hasVisibleWidgets}
					showGpsColumn={showGpsColumn}
					currentAssetCaps={currentAssetCaps}
			/>
		</div>
	);
}

export default CarDashboardTab;
